import { AtIcon, CircleInfoIcon, LinkIcon, PhoneIcon, PinIcon } from "@xola/icons";
import { ComboBox, Label, Modal, Phone, SubmitButton, Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import { requiredString, SecondaryButton, SwitchField } from "common";
import React from "react";
import { useMutation } from "react-query";
import { Except } from "type-fest";
import {
    PartnerObject,
    PurchaseRuleLink,
    Seller,
    XolaPartner,
    XolaPartnerConnectionType,
    XolaPartnerContextSeller,
    XolaPartnerStatus,
} from "types";
import { boolean, object, string } from "yup";
import { api } from "@/api/api";
import { NoTranslate } from "@/components/Common/NoTranslate";
import { XolaFormik } from "@/components/Common/XolaFormik";
import { useSeller } from "@/hooks/useAccount";
import { useAutoFetchCollection } from "@/hooks/useAutoFetchCollection";
import { useInvalidate } from "@/hooks/useInvalidation";
import { getDefaultLogoUrl } from "@/models/Seller";

interface CreatePartnerConnectionModalProps {
    isOpen: boolean;
    partnerSeller: Seller | XolaPartnerContextSeller;
    onClose: () => void;
}

export const CreatePartnerConnectionModal = ({ isOpen, partnerSeller, onClose }: CreatePartnerConnectionModalProps) => {
    return (
        <Modal size="huge" isOpen={isOpen} onClose={onClose}>
            <ModalContent partnerSeller={partnerSeller} onClose={onClose} />
        </Modal>
    );
};

const ModalContent = ({ partnerSeller, onClose }: Except<CreatePartnerConnectionModalProps, "isOpen">) => {
    const website = partnerSeller.website ?? "";
    const seller = useSeller();
    const invalidate = useInvalidate();

    const address = [partnerSeller.streetAddress, partnerSeller.city, partnerSeller.state].join(", ");
    const { aboutBusiness, phone, email } = partnerSeller?.preferences?.network ?? {};

    const { data: termsTemplates } = useAutoFetchCollection<PurchaseRuleLink>(["purchaseRuleLinks"], {
        "tags._id": "Affiliate",
    });

    const { mutateAsync: createPartner, isLoading: isCreatingPartner } = useMutation(async (payload: XolaPartner) => {
        return api.post(`/partners`, payload);
    });

    const handleSubmit = async (formValues: PartnerConnectionFormValues) => {
        const { seller, partnerSeller, sellerResells, partnerResells } = formValues;
        let connectionType;
        if (sellerResells && partnerResells) {
            connectionType = XolaPartnerConnectionType.SUPPLIER_RESELLER;
        } else if (sellerResells) {
            connectionType = XolaPartnerConnectionType.SUPPLIER;
        } else {
            connectionType = XolaPartnerConnectionType.RESELLER;
        }

        const payload: any = {
            object: PartnerObject.XOLA_PARTNER,
            status: XolaPartnerStatus.PENDING,
            seller,
            partnerSeller,
            connectionType,
        };

        if (
            connectionType === XolaPartnerConnectionType.RESELLER ||
            connectionType === XolaPartnerConnectionType.SUPPLIER_RESELLER
        ) {
            payload.purchaseRuleLink = { id: formValues.purchaseRuleLink };
        }

        await createPartner(payload);
        invalidate("partners");
        onClose();
    };

    return (
        <XolaFormik
            enableReinitialize
            validateOnChange={false}
            validationSchema={getValidationSchema()}
            initialValues={getInitialValues(seller, partnerSeller)}
            onSubmit={handleSubmit}
        >
            {({ values, errors, setFieldValue, handleSubmit }) => (
                <>
                    <Modal.Header>{partnerSeller.name}</Modal.Header>
                    <Modal.Body>
                        <div className="space-y-6">
                            <div className="space-y-4">
                                <p className="text-lg font-bold">Basic Information</p>
                                <div className="flex space-x-2">
                                    <img
                                        alt={`Logo for ${partnerSeller.name}`}
                                        src={getDefaultLogoUrl(partnerSeller, "small")}
                                        className="no-translate h-15 w-15 rounded"
                                    />
                                    <div>
                                        <NoTranslate as="p" className="text-md font-bold">
                                            {partnerSeller.name}
                                        </NoTranslate>
                                        {website ? (
                                            <p className="space-x-1">
                                                <LinkIcon />
                                                <NoTranslate>{website}</NoTranslate>
                                            </p>
                                        ) : null}
                                        {address ? (
                                            <p className="space-x-1">
                                                <PinIcon />
                                                <NoTranslate>{address}</NoTranslate>
                                            </p>
                                        ) : null}
                                    </div>
                                </div>
                            </div>
                            <div className="">
                                <p className="text-lg font-bold">About the Business</p>
                                <p>{aboutBusiness}</p>
                            </div>
                            <div className="">
                                <p className="text-lg font-bold">Contact Details</p>
                                <NoTranslate as="p">
                                    <PhoneIcon className="mr-1" />
                                    <Phone>{phone}</Phone>
                                </NoTranslate>
                                <NoTranslate as="p">
                                    <AtIcon className="mr-1" />
                                    {email}
                                </NoTranslate>
                            </div>
                            <div className="space-y-4">
                                <p className="text-lg font-bold">Partnership Settings</p>
                                <SwitchField
                                    isChecked={values.sellerResells}
                                    onChange={(value) => setFieldValue("sellerResells", value)}
                                >
                                    I want to sell their products
                                </SwitchField>
                                <div>
                                    <SwitchField
                                        isChecked={values.partnerResells}
                                        onChange={(value) => setFieldValue("partnerResells", value)}
                                    >
                                        I want them to sell my products
                                    </SwitchField>
                                </div>
                                {values.partnerResells ? (
                                    <div className="ml-10">
                                        <Label className={clsx(errors?.purchaseRuleLink && "!text-danger")}>
                                            Distribution Terms {errors.purchaseRuleLink}
                                            <Tooltip content="These are your discount and commission terms that apply to this operator when they distribute your products.">
                                                <CircleInfoIcon className="ml-1" />
                                            </Tooltip>
                                        </Label>
                                        <ComboBox
                                            placeholder="Select Terms template"
                                            appendTo={document.body}
                                            value={termsTemplates.find(
                                                (template) => template.id === values.purchaseRuleLink,
                                            )}
                                            menuPortalTarget={document.body}
                                            options={termsTemplates}
                                            getOptionValue={(option: PurchaseRuleLink) => option.id}
                                            getOptionLabel={(option: PurchaseRuleLink) => option.name}
                                            classNames={{
                                                option: () => "no-translate",
                                                singleValue: () => "no-translate",
                                                menuList: () => (termsTemplates?.length > 4 ? "h-40" : ""),
                                            }}
                                            styles={{
                                                menuPortal: (base: any) => ({
                                                    ...base,
                                                    zIndex: 30,
                                                    display: "block",
                                                }),
                                            }}
                                            onChange={(template: PurchaseRuleLink) =>
                                                setFieldValue("purchaseRuleLink", template.id)
                                            }
                                        />
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    </Modal.Body>

                    <Modal.Footer>
                        <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
                        <SubmitButton
                            type="submit"
                            disabled={!values.partnerResells && !values.sellerResells}
                            isLoading={isCreatingPartner}
                            onClick={handleSubmit}
                        >
                            Request Connections
                        </SubmitButton>
                    </Modal.Footer>
                </>
            )}
        </XolaFormik>
    );
};

interface PartnerConnectionFormValues {
    seller: { id: ID };
    partnerSeller: { id: ID };
    sellerResells: boolean;
    partnerResells: boolean;
    purchaseRuleLink?: ID;
}

const getInitialValues = (
    seller: Seller,
    partnerSeller: Seller | XolaPartnerContextSeller,
): PartnerConnectionFormValues => {
    const sellerResells = false;
    const partnerResells = false;
    return {
        seller: { id: seller.id },
        partnerSeller: { id: partnerSeller.id },
        sellerResells,
        partnerResells,
    };
};

const getValidationSchema = () => {
    return object({
        sellerResells: boolean().required(),
        partnerResells: boolean().required(),
        purchaseRuleLink: string().when("partnerResells", {
            is: true,
            then: requiredString("is required"),
            otherwise: string().notRequired(),
        }),
    });
};
